<template>
    <div>
        <div class="display-2 mt-12">
            Registration
        </div>
        <div
            v-if="!thankyou"
            class="subtitle-1 my-4"
        >
            Please select the appropriate offer.
            We will contact you as soon as possible.
        </div>
        <v-card
            flat
            class="pa-12"
        >
            <v-card-text>
                <div v-if="thankyou">
                    <p class="text-h6">
                        Thank you for your interest in the offer. You will receive a confirmation email shortly.
                    </p>
                    <p>
                        We will check your details and get in touch with you as soon as possible.
                        <br>
                        If you would like to register for another offer, please select an additional offer below.
                        <br>
                        Kind regards<br>
                        Your bewerbeagentur Team<br>
                    </p>
                </div>
                <v-alert
                    v-for="(error, errorKey) in generalFormErrors"
                    :key="errorKey"
                    type="error"
                    v-text="error"
                />
                <v-form @submit.prevent="submit">
                    <p
                        class="headline mb-8"
                        v-text="'Select offer'"
                    />
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="data.course"
                                :items="computedCourses"
                                :label="'Offer'"
                                :error-messages="formError(formErrors, 'course')"
                                :hint="'Mandatory field'"
                                persistent-hint
                                @change="courseSelected"
                            />
                            <p>
                                <br>If you are interested in more than one offer, you can select another offer after you have submitted your initial registration for a offer.
                            </p>
                        </v-col>
                    </v-row>
                    <div v-if="formType">
                        <div
                            class="mb-8"
                            v-if="computedRegistrationTimeSlots.length > 0"
                        >
                            <p
                                class="headline mb-3"
                                v-text="'Initial consultation'"
                            />
                            <v-checkbox
                                v-model="data.hadFirstInterviewViaPhone"
                                :label="'I already had an initial consultation'"
                            />
                            <v-select
                                v-model="data.registration_time_slot"
                                :items="computedRegistrationTimeSlots"
                                :label="'Choose a preferred date for your initial consultation'"
                                :hint="'Mandatory field'"
                                :disabled="data.hadFirstInterviewViaPhone === true"
                                persistent-hint
                            />
                        </div>

                        <p
                            class="headline mb-3"
                            v-text="'Personal details'"
                        />

                        <StandardForm
                            :data="data"
                            :form-errors-prop="formErrors"
                            :insurance-funds="computedInsuranceFunds"
                            :cantons="computedCantons"
                            :registration-time-slots="computedRegistrationTimeSlots"
                            :rav-locations="computedRavLocations"
                            :amms="computedAmms"
                            :socialServices="computedSocialServices"
                        />

                        <div
                            class="file-upload"
                        >
                            <p
                                class="headline mb-3"
                                v-text="'Documents'"
                            />
                            <p
                                class="text--secondary mb-3"
                            >
                                For the application we need a current CV in PDF format.
                            </p>
                            <v-row>
                                <v-col cols="12">
                                    <v-file-input
                                        v-model="file"
                                        color="primary"
                                        :label="'Curriculum Vitae (Resume)'"
                                        :placeholder="'Select a file'"
                                        :hint="'Mandatory field'"
                                        persistent-hint
                                        prepend-inner-icon="mdi-paperclip"
                                        prepend-icon=""
                                        outlined
                                        :show-size="1000"
                                        :error-messages="formError(formErrors, 'upload')"
                                        @change="fileProvided"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                        <v-row>
                            <v-col cols="12">
                                <VueRecaptcha
                                    sitekey="6LcBRO0dAAAAAPpRb0d8_EACxVB-rEN05JZxcPku"
                                    :load-recaptcha-script="true"
                                    @verify="disabled = false"
                                    @expired="disabled = true"
                                    language="en"
                                />
                            </v-col>
                        </v-row>
                        <p
                            v-if="hasError"
                            class="error--text mb-3"
                        >
                            An error has occurred, please check your entry in the fields above.
                        </p>
                    </div>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    v-if="formType"
                    color="secondary--dark"
                    class="px-4"
                    :dark="!disabled && (!enableFileCheckbox || provideFileLater)"
                    rounded
                    :disabled="disabled"
                    :loading="loading"
                    depressed
                    type=""
                    @click="submit"
                >
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>

import VueRecaptcha from 'vue-recaptcha';
import FormDataMixin from '@/mixins/form-data';
import DatesMixin from '@/mixins/dates';
import FirstInterviewMixin from '@/mixins/firstInterview';
import { StandBy } from '@/app-events.js';
import FirstInterviewProxy from '@/proxies/FirstInterviewProxy';
import InsuranceFundProxy from '@/proxies/InsuranceFundProxy';
import RavProxy from '@/proxies/RavProxy';
import AmmProxy from '@/proxies/AmmProxy';
import SocialServiceProxy from '@/proxies/SocialServiceProxy';
import StandardForm from '@/views/FirstInterviewEn/Form/StandardForm';
import { mapGetters } from 'vuex';

export default {
    name: 'FirstInterviewIndex',
    mixins: [
        FormDataMixin,
        FirstInterviewMixin,
        DatesMixin,
    ],
    components: {
        VueRecaptcha,
        StandardForm,
    },
    data() {
        return {
            loading: false,
            disabled: true,
            fileOk: false,
            provideFileLater: false,
            enableFileCheckbox: true,
            hasError: false,
            thankyou: false,
            formType: null,
            file: undefined,
            courses: [],
            registrationTimeSlots: [],
            hadFirstInterviewViaPhone: false,
            data: {
                canton: {},
                insurance_fund: {},
                rav_location: {},
                course: {},
                amm: {},
                social_service: {},
                // Declared so Vue tracks it: the "other" freetext field is rendered
                // by a v-if on this value and would not appear otherwise.
                job_category_extended: null,
            },
            insuranceFunds: [],
            ravLocations: [],
            amms: [],
            socialServices: [],
        };
    },
    created() {
        this.$store.dispatch('canton/load');
        new InsuranceFundProxy().all().then(({ data }) => {
            this.insuranceFunds = data.data;
        }).catch(({ data }) => {
            this.displayError(data);
        });
        new RavProxy().all().then(({ data }) => {
            this.ravLocations = data.data;
        }).catch(({ data }) => {
            this.displayError(data);
        });
        new AmmProxy().all().then(({ data }) => {
            this.amms = data.data;
        }).catch(({ data }) => {
            this.displayError(data);
        });
        new SocialServiceProxy().all().then(({ data }) => {
            this.socialServices = data.data;
        }).catch(({ data }) => {
            this.displayError(data);
        });
        new FirstInterviewProxy().courses('en').then(({ data }) => {
            this.courses = data.data;
        }).catch(({ data }) => {
            this.displayError(data);
        });
    },
    computed: {
        ...mapGetters({
            cantons: 'canton/cantons',
        }),
        computedAmms() {
            const allAmms = [];
            for (let i = 0; i < this.amms.length; i++) {
                const amm = this.amms[ i ];
                allAmms.push({
                    value: amm.id,
                    text: `${amm.name} (${amm.city})`,
                });
            }
            allAmms.push({
                value: 'other',
                text: 'Other',
            });
            return allAmms;
        },
        computedSocialServices() {
            const allSoz = [];
            for (let i = 0; i < this.socialServices.length; i++) {
                const soz = this.socialServices[ i ];
                allSoz.push({
                    value: soz.id,
                    text: `${soz.office_name}` + (soz.city ? ` (${soz.city})` : ''),
                });
            }
            allSoz.push({
                value: 'other',
                text: 'Other',
            });
            return allSoz;
        },
        computedCantons() {
            const allCantons = [];
            for (let i = 0; i < this.cantons.length; i++) {
                const canton = this.cantons[ i ];
                allCantons.push({
                    value: canton.code,
                    text: `${canton.code} ${canton.name}`,
                })
            }
            return allCantons;
        },
        computedRegistrationTimeSlots() {
            const allRegistrationTimeSlots = [];
            for (let i = 0; i < this.registrationTimeSlots.length; i++) {
                const registrationTimeSlot = this.registrationTimeSlots[ i ];
                const startParts = registrationTimeSlot.start.split(' ');
                allRegistrationTimeSlots.push({
                    value: registrationTimeSlot.id,
                    text: `${this.formatDate(startParts[ 0 ])} - ${startParts[ 1 ]}`,
                })
            }
            allRegistrationTimeSlots.push({
                value: '',
                text: 'No suitable time window',
            });

            return allRegistrationTimeSlots;
        },
        computedCourses() {
            const allCourses = [];
            for (let i = 0; i < this.courses.length; i++) {
                const course = this.courses[ i ];
                allCourses.push({
                    value: course,
                    text: `${course.name} (${this.formatDate(course.start_date)} - ${this.formatDate(course.end_date)})`,
                })
            }
            return allCourses;
        },
        computedInsuranceFunds() {
            const allInsuranceFunds = [];
            for (let i = 0; i < this.insuranceFunds.length; i++) {
                const insuranceFund = this.insuranceFunds[ i ];
                if (insuranceFund.name || insuranceFund.number) {
                    allInsuranceFunds.push({
                        value: insuranceFund.id,
                        text: `${insuranceFund.number} ${insuranceFund.name}`,
                    });
                }
            }
            allInsuranceFunds.push({
                value: 'other',
                text: 'Other',
            });
            return allInsuranceFunds;
        },
        computedRavLocations() {
            const allRavLocations = [];
            for (let i = 0; i < this.ravLocations.length; i++) {
                const ravLocation = this.ravLocations[ i ];
                allRavLocations.push({
                    value: ravLocation.id,
                    text: `${ravLocation.city} ${ravLocation.name}`,
                });
            }
            allRavLocations.push({
                value: 'other',
                text: 'Other',
            });
            return allRavLocations;
        },
    },
    methods: {
        fileProvided() {
            this.enableFileCheckbox = typeof this.file === 'undefined';
            this.provideFileLater = false;
        },
        submitButtonDisabled() {
            if (this.disabled) {

            }
            return false;
        },
        courseSelected() {
            this.registrationTimeSlots = [];
            if (typeof this.data.course === 'undefined') {
                this.formType = null;
                return;
            }
            this.formType = 'standard';
            this.fetchTimeSlots();
        },
        fetchTimeSlots() {
            new FirstInterviewProxy().registrationTimeSlotsForCourse(this.data.course.id).then(({ data }) => {
                this.registrationTimeSlots = data.data;
            }).catch(({ data }) => {
                this.displayError(data);
            });
        },
        submit() {
            this.loading = true;
            this.formErrors = {};
            this.$eventBus.dispatch(new StandBy(true));
            let formData = new FormData();
            let formName = 'first_interview';
            if (typeof this.file !== 'undefined') {
                formData.append(`${formName}[upload]`, this.file, this.file.name);
            } else {
                formData.append(`${formName}[upload]`, null);
            }
            const preparedData = this.prepareFormData(this.data);
            for (var key in preparedData) {
                if (preparedData.hasOwnProperty(key)) {
                    if (typeof preparedData[ key ] !== 'undefined') {
                        formData.append(`${formName}[${key}]`, preparedData[ key ]);
                    }
                }
            }
            if (!formData.has(`${formName}[firstname]`)) {
                formData.append(`${formName}[firstname]`, '');
            }
            new FirstInterviewProxy().new(formData).then(({ data }) => {
                this.hasError = false;
                this.loading = false;
                this.$eventBus.dispatch(new StandBy(false));
                //this.$router.push({ name: 'first_interview_thank_you' });
                this.data.course = {};
                this.data.registration_time_slot = '';
                this.formErrors = {};
                this.generalFormErrors = {};
                this.formType = null;
                this.thankyou = true;
                window.scrollTo(0, 0);
            }).catch(({ data }) => {
                this.loading = false;
                this.hasError = true;
                this.processErrors(data);
                this.$eventBus.dispatch(new StandBy(false));
            });
        },
    },
};
</script>
